import type { EventKind } from './event-form.js';
import type { VoyageReport } from './types.js';

/**
 * Leg / Event ekranının saf yardımcıları (M6f G4-51, G4-53, G4-60): olay türü kategorileri,
 * olayların bacaklara ayrılması ve sıra uyarıları. Kesin sıra kuralı sunucudadır
 * (`checkReportSequence`, ADR 0022 §1); buradakiler yalnız göstergedir.
 */

/** Add Event menüsünün grupları (müşterinin rapor alan matrisindeki aileler). */
export const EVENT_CATEGORIES: readonly { key: string; kinds: readonly EventKind[] }[] = [
  // Limanda günlük Berthing'in yanında (ADR 0059 §1)
  { key: 'port', kinds: ['departure', 'arrival', 'port_daily'] },
  { key: 'passage', kinds: ['cosp', 'eosp'] },
  {
    key: 'anchorage',
    kinds: [
      'anchorage_start',
      'anchorage_daily',
      'anchorage_end',
      'waiting_start',
      'waiting_end',
      'drift_start',
      'drift_daily',
      'drift_end',
    ],
  },
  { key: 'straitCanal', kinds: ['strait_entry', 'strait_exit', 'canal_entry', 'canal_exit'] },
  { key: 'commercial', kinds: ['cargo_start', 'cargo_complete', 'on_hire', 'off_hire'] },
];

const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

export interface LegGroup {
  key: string;
  /** `departure`: ilk COSP'tan önce; `passage`: bir COSP ve sonraki COSP'a kadar; `arrival`. */
  kind: 'departure' | 'passage' | 'arrival';
  /** Bacak numarası (COSP sırası); kalkış ve varışta `null`. */
  leg: number | null;
  /** Seyir sürüyor: bacakta COSP'tan sonra EOSP yok. */
  open: boolean;
  items: VoyageReport[];
}

/**
 * Olayların bacaklara ayrılması (G4-53): ilk COSP'tan önceki olaylar kalkış; her COSP yeni bacak
 * başlatır ve sonraki COSP'a kadarki olaylar (EOSP, demir, bekleme …) o bacağındır; varış ve
 * sonrası ayrı grup. Zamana göre artan.
 */
export function legGroups(events: readonly VoyageReport[]): LegGroup[] {
  const groups: LegGroup[] = [];
  let leg = 0;
  for (const event of [...events].sort(byTime)) {
    const last = groups.at(-1);
    // İptal edilen olay (ADR 0077 §3) izde, bulunduğu yerde görünür; bacak açmaz ve kapatmaz
    if (event.status === 'void') {
      if (last) last.items.push(event);
      else
        groups.push({
          key: 'departure',
          kind: 'departure',
          leg: null,
          open: false,
          items: [event],
        });
      continue;
    }
    if (event.kind === 'cosp') {
      leg += 1;
      groups.push({ key: `leg-${leg}`, kind: 'passage', leg, open: true, items: [event] });
      continue;
    }
    if (event.kind === 'arrival' && last?.kind !== 'arrival') {
      if (last?.kind === 'passage') last.open = false;
      groups.push({ key: 'arrival', kind: 'arrival', leg: null, open: false, items: [event] });
      continue;
    }
    if (!last) {
      groups.push({ key: 'departure', kind: 'departure', leg: null, open: false, items: [event] });
      continue;
    }
    if (event.kind === 'eosp' && last.kind === 'passage') last.open = false;
    last.items.push(event);
  }
  return groups;
}

export interface EventAlert {
  code: 'OPEN_PASSAGE' | 'DRAFT_PENDING' | 'MISSING_POSITION';
  tone: 'info' | 'warning';
  report: VoyageReport | null;
}

/**
 * Sıra göstergeleri (G4-60): EOSP'si gelmemiş açık seyir (bilgi), gönderilmemiş taslak ve konumu
 * girilmemiş olay (uyarı). Noon raporları kendi ekranında.
 */
export function eventAlerts(events: readonly VoyageReport[]): EventAlert[] {
  // İptal edilen olay (ADR 0077 §3) uyarı üretmez
  const sorted = [...events].filter((r) => r.kind !== 'noon' && r.status !== 'void').sort(byTime);
  const alerts: EventAlert[] = [];
  const open = legGroups(sorted)
    .filter((g) => g.kind === 'passage' && g.open)
    .at(-1);
  if (open) alerts.push({ code: 'OPEN_PASSAGE', tone: 'info', report: open.items[0] ?? null });
  for (const event of sorted) {
    if (event.status !== 'submitted') {
      alerts.push({ code: 'DRAFT_PENDING', tone: 'warning', report: event });
    }
  }
  for (const event of sorted) {
    if (event.lat === null || event.lon === null) {
      alerts.push({ code: 'MISSING_POSITION', tone: 'warning', report: event });
    }
  }
  return alerts;
}
